import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, Volume2, VolumeX, Calendar as CalendarIcon, Send, MessageCircle } from "lucide-react";
import DoozyBotSVG from "./DoozyBotSVG";
import { playBoop, isSoundMuted, toggleSoundMuted } from "../utils/doozybotSound";
import { WHATSAPP_LINK } from "../constants";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GREETING =
  "Hi! I'm Doozy 🎉 Ask me anything about our packages, occasions, areas or how it all works. When you're ready to book, tap \u201cBook Online\u201d or \u201cBook on WhatsApp\u201d below.";

const newSessionId = () => `web_${Math.random().toString(36).slice(2, 10)}`;

const DoozyBotWidget = () => {
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(isSoundMuted());
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionIdRef = useRef(newSessionId());
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  const toggleOpen = () => {
    setOpen((o) => {
      const next = !o;
      if (next && !muted) playBoop();
      return next;
    });
  };

  const toggleMute = () => {
    const m = toggleSoundMuted();
    setMuted(m);
  };

  const goBookOnline = () => {
    setOpen(false);
    const el = document.getElementById("book-online");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.location.href = "/#book-online";
  };

  const openWhatsApp = () => {
    window.open(WHATSAPP_LINK, "_blank", "noopener,noreferrer");
  };

  const send = useCallback(
    async (text) => {
      const message = (text ?? input).trim();
      if (!message || sending) return;
      setInput("");
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      setMessages((prev) => [...prev, { role: "user", content: message }]);
      setSending(true);
      try {
        const res = await fetch(`${API_URL}/api/doozybot-public/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message, history, session_id: sessionIdRef.current }),
        });
        const data = await res.json();
        const reply =
          data?.reply ||
          "Sorry, I had a small hiccup. Please try again, or reach us on WhatsApp — we're always happy to help!";
        setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
        if (!muted) playBoop();
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "I'm having trouble connecting right now. Please tap \u201cBook on WhatsApp\u201d below and we'll help you directly!",
          },
        ]);
      } finally {
        setSending(false);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    },
    [input, sending, messages, muted]
  );

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={toggleOpen}
          className="fixed bottom-5 right-5 z-[60] w-16 h-16 rounded-full shadow-2xl bg-white ring-2 ring-[#DC283C]/20 hover:scale-105 active:scale-95 transition-transform flex items-center justify-center"
          aria-label="Open DoozyBot chat"
          data-testid="doozybot-fab"
        >
          <DoozyBotSVG className="w-12 h-12" />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-[60] w-[92vw] max-w-[380px] h-[70vh] max-h-[560px] bg-white rounded-3xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden"
          data-testid="doozybot-chat-window"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#DC283C] to-[#FF6B4A] text-white flex-shrink-0">
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
              <DoozyBotSVG className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm leading-tight">DoozyBot</p>
              <p className="text-[11px] text-white/85 leading-tight">Ask me anything · replies in seconds</p>
            </div>
            <button onClick={toggleMute} className="p-1.5 rounded-lg hover:bg-white/15 transition" aria-label={muted ? "Unmute" : "Mute"} data-testid="doozybot-mute">
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            <button onClick={toggleOpen} className="p-1.5 rounded-lg hover:bg-white/15 transition" aria-label="Close chat" data-testid="doozybot-close">
              <X size={18} />
            </button>
          </div>

          {/* Booking quick actions */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 bg-[#FFF7F5] flex-shrink-0">
            <button
              onClick={goBookOnline}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#DC283C] text-white text-xs font-bold hover:bg-[#c22035] transition-colors"
              data-testid="doozybot-book-online"
            >
              <CalendarIcon size={14} /> Book Online
            </button>
            <button
              onClick={openWhatsApp}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#25D366] text-white text-xs font-bold hover:bg-[#1eb558] transition-colors"
              data-testid="doozybot-book-whatsapp"
            >
              <MessageCircle size={14} /> Book on WhatsApp
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3.5 py-3 space-y-3 bg-[#FAFAFA]" data-testid="doozybot-messages">
            {messages.map((m, i) => (
              <div key={m.id || `${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[82%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#DC283C] text-white rounded-br-md"
                      : "bg-white text-gray-800 border border-gray-100 rounded-bl-md shadow-sm"
                  }`}
                  data-testid={`doozybot-msg-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="doozybot-typing">
                <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-md px-3.5 py-2.5 shadow-sm">
                  <span className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-t border-gray-100 flex-shrink-0">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your question…"
              maxLength={500}
              disabled={sending}
              className="flex-1 px-3.5 py-2.5 rounded-full bg-[#F0F0F0] border border-transparent text-sm outline-none focus:border-[#DC283C]/40 focus:bg-white transition-colors disabled:opacity-60"
              data-testid="doozybot-input"
            />
            <button
              onClick={() => send()}
              disabled={sending || !input.trim()}
              className="w-10 h-10 flex-shrink-0 rounded-full bg-[#DC283C] text-white flex items-center justify-center hover:bg-[#c22035] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Send message"
              data-testid="doozybot-send"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DoozyBotWidget;
